// The Manuals library's "Who made each document" card (Session BBX2). A document's maker is read from what the document says
// about itself and kept beside its folder name, never over it. Here the office:
//   - checks and then updates the identity of every document (previewed first, saved through a two-step confirm);
//   - reviews the documents the text could not settle: approve the proposed maker, change it, or mark it as not a manual.
// Every decision is audited and reversible ("Clear decision"). Nothing is read from Drive and no vision is spent.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Button } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Loader } from "../../components/Loader";
import { inputCls, labelCls } from "./knowledgeUi";
import type { KnowledgeMakerEvidence, KnowledgeMakerIdentitySummary, KnowledgeMakerReviewItem } from "../../lib/knowledgeTypes";

const QUIET_BTN =
  "rounded-ele border border-ele-purple-accent px-3 py-1.5 text-sm text-ele-strong hover:bg-ele-surface-raised disabled:opacity-50";

const IDENTITY_WORDS: Array<[string, string]> = [
  ["confirmed", "Confirmed (two signals agree)"],
  ["single", "One signal"],
  ["conflicting", "Conflicting"],
  ["none", "No maker found"],
  ["not_derived", "Not read yet"],
];

function evidenceLine(e: KnowledgeMakerEvidence): string {
  const where = e.page ? `page ${e.page}` : e.signal === "header_footer" ? "a running header or footer" : "the file name";
  return `"${e.phrase}" (${where})`;
}

export function KnowledgeMakerIdentityCard() {
  const [summary, setSummary] = useState<KnowledgeMakerIdentitySummary | null>(null);
  const [items, setItems] = useState<KnowledgeMakerReviewItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [changing, setChanging] = useState<{ id: string; maker: string } | null>(null);
  const [showDecided, setShowDecided] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([api.knowledge.drive.makerIdentity(), api.knowledge.drive.makerReview(50, 0, showDecided)]);
      setSummary(s);
      setItems(r.items);
      setTotal(r.total);
      setError(null);
    } catch (err) {
      setError(friendlyApiError(err, "The maker identity could not be loaded."));
    }
  }, [showDecided]);

  useEffect(() => {
    void load();
  }, [load]);

  async function backfill(preview: boolean) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.knowledge.drive.makerBackfill(preview);
      if (preview) setPreviewText(res.message);
      else {
        setMessage(res.message);
        setPreviewText(null);
        await load();
      }
    } catch (err) {
      setError(friendlyApiError(err, "The identity could not be read."));
    } finally {
      setBusy(false);
    }
  }

  async function decide(id: string, body: { action: "approve" | "not_manual" | "reset" } | { action: "change"; maker: string }) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.knowledge.drive.makerDecide(id, body, false);
      setMessage(res.message);
      setChanging(null);
      await load();
    } catch (err) {
      setError(friendlyApiError(err, "The decision could not be saved."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4" aria-labelledby="kb-maker-heading">
      <h2 id="kb-maker-heading" className="font-heading text-lg text-ele-blue">
        Who made each document
      </h2>
      <p className="mt-1 text-sm text-ele-body">
        A search for one maker only looks at that maker&apos;s documents, so each document needs the right maker. It is read from the document&apos;s own words
        (its cover, a running header or footer, a copyright line) and kept beside the folder name, which is never changed. Nothing is read from Drive.
      </p>
      {message && (
        <p role="status" className="mt-2 text-sm text-ele-strong">
          {message}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      {summary === null ? (
        error ? null : <Loader label="Loading the maker identity" />
      ) : (
        <>
          <ul className="mt-3 grid gap-1 text-sm text-ele-strong sm:grid-cols-2">
            {IDENTITY_WORDS.map(([k, label]) => (
              <li key={k}>
                {label}: <strong>{(summary.byConfidence[k] ?? 0).toLocaleString("en-GB")}</strong>
              </li>
            ))}
            <li>
              Need a person: <strong>{summary.needsReview.toLocaleString("en-GB")}</strong>
            </li>
            <li>
              Waiting for the one-off AI pass: <strong>{summary.awaitingModelPass.toLocaleString("en-GB")}</strong>
            </li>
            <li>
              General reference (no maker named): <strong>{summary.generalReference.toLocaleString("en-GB")}</strong>
            </li>
            <li>
              Decided by a person: <strong>{summary.decided.toLocaleString("en-GB")}</strong>
            </li>
          </ul>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {!previewText ? (
              <Button type="button" onClick={() => void backfill(true)} loading={busy} disabled={busy}>
                Check every document
              </Button>
            ) : (
              <ConfirmButton label="Save the maker of every document" confirmLabel="Save them now?" onConfirm={() => backfill(false)} disabled={busy} />
            )}
            {previewText && (
              <button type="button" className={QUIET_BTN} onClick={() => setPreviewText(null)} disabled={busy}>
                Cancel
              </button>
            )}
          </div>
          {previewText && (
            <p role="status" className="mt-2 text-sm text-ele-strong">
              {previewText}
            </p>
          )}
          <p className="mt-3 text-sm text-ele-body">
            One-off AI pass on the documents the text could not settle: {summary.modelPass.documents.toLocaleString("en-GB")} documents, about USD{" "}
            {summary.modelPass.estimatedUsd}. Ask an admin (or Guvnor) to run it; every answer is only a proposal for the list below.
          </p>
        </>
      )}

      {items !== null && (
        <div className="mt-4">
          <h3 className="font-heading text-base text-ele-strong">Documents that need a person ({total.toLocaleString("en-GB")})</h3>
          <label className="mt-1 flex items-center gap-2 text-sm text-ele-strong">
            <input type="checkbox" checked={showDecided} onChange={(e) => setShowDecided(e.target.checked)} />
            Also show the ones a person has decided, so a decision can be cleared
          </label>
          {items.length === 0 ? (
            <p className="mt-2 text-sm text-ele-body">Nothing needs a decision.</p>
          ) : (
            <ul className="mt-2 space-y-3">
              {items.map((i) => (
                <li key={i.documentId} className="rounded-ele border border-ele-purple-accent p-3 text-sm">
                  <p className="text-ele-strong">
                    <strong>{i.fileName}</strong>
                    <span className="text-ele-body">{i.folder ? ` (filed under ${i.folder})` : ""}</span>
                  </p>
                  <p className="mt-1 text-ele-body">
                    {i.confidence === "conflicting" ? "The text names more than one maker." : "The text names no maker."}
                    {i.evidence.length > 0 && <> Read from: {i.evidence.map(evidenceLine).join("; ")}.</>}
                  </p>
                  <p className="mt-1 text-ele-strong">
                    {i.proposedMaker ? (
                      <>
                        Proposed maker: <strong>{i.proposedMaker}</strong>
                        {i.proposedBy === "model" ? " (an AI suggestion)" : ""}
                        {i.proposedEvidence.length > 0 && <> from {i.proposedEvidence.map(evidenceLine).join("; ")}</>}
                      </>
                    ) : (
                      "No maker proposed."
                    )}
                  </p>
                  {i.decision && (
                    <p className="mt-1 text-ele-strong">
                      Decided: {i.decision === "not_manual" ? "not a manual" : i.decision === "approved" ? "the proposed maker was approved" : "a maker was chosen"}.{" "}
                      <ConfirmButton label="Clear decision" confirmLabel="Read it from its own text again?" onConfirm={() => decide(i.documentId, { action: "reset" })} disabled={busy} />
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {i.proposedMaker && (
                      <ConfirmButton label="Approve" confirmLabel={`File it under ${i.proposedMaker}?`} onConfirm={() => decide(i.documentId, { action: "approve" })} disabled={busy} />
                    )}
                    <button type="button" className={QUIET_BTN} onClick={() => setChanging({ id: i.documentId, maker: "" })} disabled={busy}>
                      Change maker
                    </button>
                    <ConfirmButton
                      label="Not a manual"
                      confirmLabel="Leave it out of every maker's results?"
                      onConfirm={() => decide(i.documentId, { action: "not_manual" })}
                      disabled={busy}
                    />
                  </div>
                  {changing?.id === i.documentId && (
                    <div className="mt-2 flex flex-wrap items-end gap-2" role="group" aria-label={`Change the maker of ${i.fileName}`}>
                      <label className="block max-w-xs">
                        <span className={labelCls}>Maker</span>
                        <input aria-label="Maker" value={changing.maker} onChange={(e) => setChanging({ id: i.documentId, maker: e.target.value })} className={inputCls} />
                      </label>
                      <ConfirmButton
                        label="Save the maker"
                        confirmLabel="File it under this maker?"
                        onConfirm={() => decide(i.documentId, { action: "change", maker: changing.maker.trim() })}
                        disabled={busy || changing.maker.trim().length < 2}
                      />
                      <button type="button" className={QUIET_BTN} onClick={() => setChanging(null)}>
                        Cancel
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
